import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Eye } from 'lucide-react';

import { serviceReminderService } from '../../services/serviceReminderService';

const reminderOptions = [
  { value: '3_months', label: '3 Months' },
  { value: '6_months', label: '6 Months' },
  { value: '9_months', label: '9 Months' },
  { value: '1_year', label: '1 Year' },
];

const ServiceReminders = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);

  const deviceTypes = ['chimney', 'ro', 'cooktop'];
  const categoryOptionsByDevice = {
    chimney: ['service', 'installation', 'warranty'],
    ro: ['service', 'installation', 'warranty'],
    cooktop: ['service', 'installation', 'warranty'],
  };

  const [form, setForm] = useState({
    deviceType: 'chimney',
    category: 'service',
    reminderMode: 'offset', // offset | custom
    reminderOption: '3_months',
    customReminderAt: '',
    status: 'pending',
    customerName: '',
    address: '',
    customerPhone: '',
  });

  useEffect(() => {
    fetchReminders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReminders = async () => {
    try {
      const res = await serviceReminderService.getAll();
      setReminders(res?.data || res || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load service reminders');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const arr = Array.isArray(reminders) ? reminders : [];
    const q = search.trim().toLowerCase();
    if (!q) return arr;

    return arr.filter((r) => {
      return (
        r._id?.toString().toLowerCase().includes(q) ||
        r.serviceType?.toLowerCase().includes(q) ||
        r.customerName?.toLowerCase().includes(q) ||
        r.address?.toLowerCase().includes(q) ||
        r.customerPhone?.toLowerCase().includes(q)
      );
    });
  }, [reminders, search]);

  const validatePhone = (value) => /^\d{10}$/.test(value || '');

  const createReminder = async (e) => {
    e.preventDefault();

    try {
      if (!form.deviceType || !form.category) {
        toast.error('Please select device type and category');
        return;
      }

      if (!validatePhone(form.customerPhone)) {
        toast.error('Customer phone is required and must be exactly 10 digits');
        return;
      }

      const payload = {
        serviceType: `${form.deviceType}:${form.category}`,
        reminderOption: form.reminderOption,
        customReminderAt: form.reminderMode === 'custom' ? form.customReminderAt : undefined,
        customerName: form.customerName,
        address: form.address,
        customerPhone: form.customerPhone,
      };

      await serviceReminderService.create(payload);

      toast.success('Reminder created');
      setForm({
        deviceType: 'chimney',
        category: 'service',
        reminderMode: 'offset',
        reminderOption: '3_months',
        customReminderAt: '',
        status: 'pending',
        customerName: '',
        address: '',
        customerPhone: '',
      });
      fetchReminders();
    } catch (err) {
      console.error(err);
      toast.error('Failed to create reminder');
    }
  };

  const startEdit = (r) => {
    setSelected(r);
    setEditing(true);

    setForm({
      deviceType: (r.serviceType || '').split(':')?.[0] || 'chimney',
      category: (r.serviceType || '').split(':')?.[1] || 'service',
      reminderMode: r.customReminderAt ? 'custom' : 'offset',
      reminderOption: r.reminderOption || '3_months',
      customReminderAt: r.reminderAt ? new Date(r.reminderAt).toISOString().slice(0, 16) : '',
      status: r.status || 'pending',
      customerName: r.customerName || '',
      address: r.address || '',
      customerPhone: r.customerPhone || '',
    });
  };

  const closeModal = () => {
    setSelected(null);
    setEditing(false);
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Service Reminders</h1>
        <span className="px-4 py-2 bg-purple-100 text-purple-700 rounded-xl font-medium">Total: {reminders.length}</span>
      </div>

      <form onSubmit={createReminder} className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Create Reminder</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-gray-600">Device Type</label>
            <select
              value={form.deviceType}
              onChange={(e) => {
                const deviceType = e.target.value;
                setForm((f) => ({
                  ...f,
                  deviceType,
                  category: (categoryOptionsByDevice[deviceType] || ['service'])[0],
                }));
              }}
              className="w-full pl-4 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
            >
              {deviceTypes.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-600">Category</label>
            <select
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              className="w-full pl-4 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
            >
              {(categoryOptionsByDevice[form.deviceType] || []).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-600">Reminder When</label>
            <select
              value={form.reminderMode}
              onChange={(e) => setForm((f) => ({ ...f, reminderMode: e.target.value }))}
              className="w-full pl-4 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
            >
              <option value="offset">After months/years</option>
              <option value="custom">Custom date</option>
            </select>
          </div>

          {form.reminderMode === 'offset' ? (
            <div>
              <label className="text-sm text-gray-600">Offset</label>
              <select
                value={form.reminderOption}
                onChange={(e) => setForm((f) => ({ ...f, reminderOption: e.target.value }))}
                className="w-full pl-4 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              >
                {reminderOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="text-sm text-gray-600">Custom Reminder At</label>
              <input
                type="datetime-local"
                value={form.customReminderAt}
                onChange={(e) => setForm((f) => ({ ...f, customReminderAt: e.target.value }))}
                className="w-full pl-4 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              />
            </div>
          )}

          <div>
            <label className="text-sm text-gray-600">Customer Name (optional)</label>
            <input
              value={form.customerName}
              onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))}
              placeholder="Name"
              className="w-full pl-4 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
            />
          </div>

          <div>
            <label className="text-sm text-gray-600">Address (optional)</label>
            <input
              value={form.address}
              onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              placeholder="Address"
              className="w-full pl-4 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-sm text-gray-600">Customer Phone (required)</label>
            <input
              inputMode="numeric"
              value={form.customerPhone}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '');
                setForm((f) => ({ ...f, customerPhone: digits }));
              }}
              placeholder="10 digit phone"
              className="w-full pl-4 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:shadow-lg transition-all font-medium"
          >
            Create
          </button>
        </div>
      </form>

      <div className="mb-6">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by service/customer..."
          className="w-full pl-4 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
        />
      </div>

      <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
              <tr>
                <th className="px-6 py-4 text-left">Reminder</th>
                <th className="px-6 py-4 text-left">Service</th>
                <th className="px-6 py-4 text-left">Customer</th>
                <th className="px-6 py-4 text-left">Option</th>
                <th className="px-6 py-4 text-left">Status</th>
                <th className="px-6 py-4 text-left">Reminder At</th>
                <th className="px-6 py-4 text-center">Details</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((r) => (
                <tr key={r._id} className="border-b hover:bg-purple-50 transition-colors">
                  <td className="px-6 py-4 font-mono text-sm text-gray-600">{r._id?.slice(-8)}</td>
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-800">
                      {r.serviceType?.split(':')?.[0] || r.serviceType} - {r.serviceType?.split(':')?.[1] || ''}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-800">{r.customerName || '-'}</div>
                    <div className="text-sm text-gray-500">{r.customerEmail || r.customerPhone || '-'}</div>
                  </td>
                  <td className="px-6 py-4">{reminderOptions.find((o) => o.value === r.reminderOption)?.label || r.reminderOption}</td>
                  <td className="px-6 py-4">
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">{r.status}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{r.reminderAt ? new Date(r.reminderAt).toLocaleString() : '-'}</td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => {
                          setSelected(r);
                          setEditing(false);
                        }}
                        className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                      >
                        <Eye size={18} />
                      </button>

                      <button
                        onClick={() => startEdit(r)}
                        className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filtered.length === 0 && <div className="p-6 text-center text-gray-500">No reminders found.</div>}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-8 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-xl transition-colors"
            >
              ×
            </button>

            <h2 className="text-2xl font-bold text-gray-800 mb-6">Service Reminder Details</h2>

            {editing ? (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    if (!validatePhone(form.customerPhone)) {
                      toast.error('Customer phone is required and must be exactly 10 digits');
                      return;
                    }

                    await serviceReminderService.update(selected._id, {
                      serviceType: `${form.deviceType}:${form.category}`,
                      reminderOption: form.reminderOption,
                      customReminderAt: form.reminderMode === 'custom' ? form.customReminderAt : undefined,
                      customerName: form.customerName,
                      address: form.address,
                      customerPhone: form.customerPhone,
                      status: form.status,
                    });

                    toast.success('Reminder updated');
                    setEditing(false);
                    setSelected(null);
                    fetchReminders();
                  } catch (err) {
                    console.error(err);
                    toast.error('Failed to update reminder');
                  }
                }}
                className="space-y-4 text-sm"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-gray-500">Device Type</div>
                    <select
                      value={form.deviceType}
                      onChange={(e) => {
                        const deviceType = e.target.value;
                        setForm((f) => ({
                          ...f,
                          deviceType,
                          category: (categoryOptionsByDevice[deviceType] || ['service'])[0],
                        }));
                      }}
                      className="mt-1 w-full pl-4 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none bg-white"
                    >
                      {deviceTypes.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <div className="text-gray-500">Category</div>
                    <select
                      value={form.category}
                      onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                      className="mt-1 w-full pl-4 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none bg-white"
                    >
                      {(categoryOptionsByDevice[form.deviceType] || []).map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <div className="text-gray-500">Reminder When</div>
                  <select
                    value={form.reminderMode}
                    onChange={(e) => setForm((f) => ({ ...f, reminderMode: e.target.value }))}
                    className="mt-1 w-full pl-4 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none bg-white"
                  >
                    <option value="offset">After months/years</option>
                    <option value="custom">Custom date</option>
                  </select>
                </div>

                {form.reminderMode === 'offset' ? (
                  <div>
                    <div className="text-gray-500">Offset</div>
                    <select
                      value={form.reminderOption}
                      onChange={(e) => setForm((f) => ({ ...f, reminderOption: e.target.value }))}
                      className="mt-1 w-full pl-4 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none bg-white"
                    >
                      {reminderOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div>
                    <div className="text-gray-500">Custom Reminder At</div>
                    <input
                      type="datetime-local"
                      value={form.customReminderAt}
                      onChange={(e) => setForm((f) => ({ ...f, customReminderAt: e.target.value }))}
                      className="mt-1 w-full pl-4 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none bg-white"
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-gray-500">Customer Name</div>
                    <input
                      value={form.customerName}
                      onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))}
                      placeholder="Name"
                      className="mt-1 w-full pl-4 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none bg-white"
                    />
                  </div>

                  <div>
                    <div className="text-gray-500">Status</div>
                    <select
                      value={form.status}
                      onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                      className="mt-1 w-full pl-4 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none bg-white"
                    >
                      <option value="pending">pending</option>
                      <option value="sent">sent</option>
                      <option value="installation_done">installation_done</option>
                      <option value="attended_not_resolved">attended_not_resolved</option>
                      <option value="attended_waiting_for_spares">attended_waiting_for_spares</option>
                      <option value="scheduled">scheduled</option>
                      <option value="cancelled">cancelled</option>
                    </select>
                  </div>
                </div>

                <div>
                  <div className="text-gray-500">Address</div>
                  <input
                    value={form.address}
                    onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                    placeholder="Address"
                    className="mt-1 w-full pl-4 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none bg-white"
                  />
                </div>

                <div>
                  <div className="text-gray-500">Customer Phone (required)</div>
                  <input
                    inputMode="numeric"
                    value={form.customerPhone}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '');
                      setForm((f) => ({ ...f, customerPhone: digits }));
                    }}
                    className="mt-1 w-full pl-4 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none bg-white"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="px-4 py-3 rounded-xl border border-gray-200 hover:bg-gray-50 transition-all text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:shadow-lg transition-all font-medium"
                  >
                    Save
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-gray-500">Reminder ID</div>
                    <div className="font-mono text-gray-800">{selected._id}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Status</div>
                    <div className="font-medium text-gray-800">{selected.status}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-gray-500">Service</div>
                    <div className="font-medium text-gray-800">{selected.serviceType}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Option</div>
                    <div className="font-medium text-gray-800">
                      {reminderOptions.find((o) => o.value === selected.reminderOption)?.label || selected.reminderOption}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-gray-500">Customer Name</div>
                    <div className="font-medium text-gray-800">{selected.customerName || '-'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Customer Email/Phone</div>
                    <div className="font-medium text-gray-800">{selected.customerEmail || selected.customerPhone || '-'}</div>
                  </div>
                </div>

                <div>
                  <div className="text-gray-500">Reminder At</div>
                  <div className="font-medium text-gray-800">{selected.reminderAt ? new Date(selected.reminderAt).toLocaleString() : '-'}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceReminders;

